import React, { useEffect, useState } from "react";

const VersionChecker = ({ children }) => {
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

  const checkForUpdates = async () => {
    try {
      // Add timestamp to prevent caching
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-cache",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      const serverVersion = await response.json();
      const localVersion = localStorage.getItem("appVersion");

      // First time visiting or version mismatch
      if (!localVersion) {
        localStorage.setItem("appVersion", serverVersion.version);
      } else if (localVersion !== serverVersion.version) {
        setShowUpdatePrompt(true);
      }
    } catch (error) {
      console.error("Error checking version:", error);
    }
  };

  useEffect(() => {
    // Check on mount
    checkForUpdates();

    // Set up interval checking
    const interval = setInterval(checkForUpdates, CHECK_INTERVAL);

    // Check when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForUpdates();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleUpdate = async () => {
    // Hide the prompt immediately
    setShowUpdatePrompt(false);

    try {
      // Clear all caches
      if ("caches" in window) {
        const names = await caches.keys();
        await Promise.all(names.map((name) => caches.delete(name)));
      }

      // Clear service worker
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
      }

      // Update localStorage with new version
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-cache",
      });
      const serverVersion = await response.json();
      localStorage.setItem("appVersion", serverVersion.version);

      // Check if we're in an iframe
      const isInIframe = window.self !== window.top;

      if (isInIframe) {
        // Try to notify parent window to refresh the iframe
        try {
          window.parent.postMessage({ type: "REFRESH_IFRAME" }, "*");
        } catch (e) {
          console.log("Could not notify parent window");
        }

        // Also refresh ourselves with cache buster
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set("_v", Date.now());
        window.location.href = currentUrl.toString();
      } else {
        // For regular windows: Standard reload
        window.location.href = window.location.href;
      }
    } catch (error) {
      console.error("Error during update:", error);
      // Fallback: Add cache buster and reload
      const url = new URL(window.location.href);
      url.searchParams.set("_v", Date.now());
      window.location.href = url.toString();
    }
  };

  const handleDismiss = () => {
    setShowUpdatePrompt(false);
    // Show again after some time
    setTimeout(() => checkForUpdates(), 30 * 60 * 1000); // 30 minutes
  };

  return (
    <>
      {children}

      {showUpdatePrompt && (
        <div className="fixed bottom-5 right-5 bg-tier-primary dark:bg-tier-primary-dark text-white p-4 rounded-lg shadow-xl z-[9999] max-w-md animate-slide-up">
          <div className="mb-3 font-semibold text-base">
            New Update Available!
          </div>
          <div className="mb-4 text-sm leading-relaxed">
            A new version of the app is available. Please refresh to get the
            latest features and improvements.
          </div>
          <div className="flex gap-3">
            <button onClick={handleUpdate} className="btn-action">
              Update Now
            </button>
            <button onClick={handleDismiss} className="btn-action-white">
              Later
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default VersionChecker;
